import React, { useEffect, useState } from "react";
import { useHostPortalContext } from "context/HostPortalContext";
import { useUser } from "context/UserContext";
import { useAlertContext } from "context/AlertContext";
import corporateAccountService from "services/corporateAccountService";
import locationsService from "services/locationsService";
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import Footer from "components/Footer";
import MDBox from "mdComponents/MDBox";
import MDTypography from "mdComponents/MDTypography";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import { CorporateHostStatus } from "../models/corporateHostPerformanceModel";
import { CheckIcon, XIcon } from "../components/Icons/Index";
import { useNavigate } from "react-router-dom";

interface CorporateHostPerformanceProps {
  limitToId?: number;
  hideBusinessName?: boolean;
  title?: string;
  description?: string;
  disableClick?: boolean;
  reloadKey?: number;
}


const CorporateHostPerformance = (props: CorporateHostPerformanceProps) => {
  const { limitToId, description, title, hideBusinessName, disableClick, reloadKey } = props;
  const navigate = useNavigate();
  const { setHost } = useHostPortalContext();
  const { userCorporateAccount } = useUser();
  const { setAlert } = useAlertContext();

  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [showCorporateRollup, setShowCorporateRollup] = useState(true);
  const [hostStats, setHostStats] = useState<CorporateHostStatus[]>([]);

    useEffect(() => {
        if (!userCorporateAccount?.id) return;
        loadHostStats(reloadKey !== 0);
    }, [userCorporateAccount?.id, reloadKey]);


    const loadHostStats = async (isSilentReload?: boolean) => {
        if(!isSilentReload)
            setLoading(true);

      try {
        let data = await corporateAccountService.getCorporateHostPerformance(
          userCorporateAccount?.id
        );
        if (data) {
            if(limitToId){
                data = data.filter(a => a.hostId === limitToId);
            };

          setHostStats(data.filter((a) => a.hostId !== -1));
          setShowCorporateRollup(data.filter((a) => a.hostId !== -1).length > 1);          
        } else {
          setAlert("error", "Unable to load host stats");
        }
      } catch (err) {
        console.error(err);
        setAlert("error", "Error loading host stats");
      } finally {
        setLoading(false);
      }
    };

  const statText = (value: boolean | null, useWords = false) => {
    if (value === null) return "-";
    return value ? (useWords ? "Yes" : <CheckIcon size={16} />) : useWords ? "No" : <XIcon />;
  };

  const goToManageHost = async (hostId: number) => {
    if (disableClick || hostId === -1) return;
    setRedirecting(true);
    const selectedHost = await locationsService.getHostRecord(hostId);
    if (!selectedHost) {
      setRedirecting(false);
      return;
    }
    setHost(selectedHost);
    setRedirecting(false);
    navigate("/location/" + hostId);
  };

  const renderHostCard = (item: CorporateHostStatus) => {
    const isTotalRow = item.hostId === -1;
    if (isTotalRow && !showCorporateRollup) return null;

    return (
      <Card
        key={item.hostId}
        sx={{
          p: 2,
          mb:2,
          cursor: isTotalRow ? "default" : "pointer",
          backgroundColor: isTotalRow ? "#e0f7fa" : "#fff",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
        onClick={() => goToManageHost(item.hostId)}
      >
        {!hideBusinessName &&
        <MDTypography variant="h6" fontWeight="bold" mb={2}>
          {isTotalRow ? "Corporate Total" : item.hostName}
        </MDTypography>}

        <Grid container spacing={1}>
          <Grid item xs={4}>
            <MDBox display="flex" flexDirection="column" alignItems="center">
              <MDTypography sx={{fontSize: '0.8em'}}>{statText(item.isApproved)}</MDTypography>
              <MDTypography variant="caption" textAlign="center">
                Approved (active)
              </MDTypography>
            </MDBox>
          </Grid>
          <Grid item xs={4}>
            <MDBox display="flex" flexDirection="column" alignItems="center">
              <MDTypography>{statText(item.hostPremiumOnly)}</MDTypography>
              <MDTypography variant="caption" textAlign="center">
                Host Premium Users Only
              </MDTypography>
            </MDBox>
          </Grid>
          <Grid item xs={4}>
            <MDBox display="flex" flexDirection="column" alignItems="center">
              <MDTypography>{statText(item.freeForever)}</MDTypography>
              <MDTypography variant="caption" textAlign="center">
                Free Forever
              </MDTypography>
            </MDBox>
          </Grid>
        </Grid>

        <Grid container spacing={2} mt={.5}>
          <Grid item xs={4}>
            <MDBox display="flex" flexDirection="column" alignItems="center">
              <MDTypography>{statText(item.currentlyOpen)}</MDTypography>
              <MDTypography variant="caption" textAlign="center">
                Currently Open
              </MDTypography>
            </MDBox>
          </Grid>
          <Grid item xs={4}>
            <MDBox display="flex" flexDirection="column" alignItems="center">
              <MDTypography>{statText(item.doorLockCodeRequired)}</MDTypography>
              <MDTypography variant="caption" textAlign="center">
                Door Lock Required
              </MDTypography>
            </MDBox>
          </Grid>
          <Grid item xs={4}>
            <MDBox display="flex" flexDirection="column" alignItems="center">
              <MDTypography
                sx={{ fontWeight: "bold", color: item.closedForCleaning ? "red" : "inherit" }}
              >
                {statText(item.closedForCleaning, true)}
              </MDTypography>
              <MDTypography variant="caption" textAlign="center">
                Closed for Cleaning
              </MDTypography>
            </MDBox>
          </Grid>
        </Grid>
      </Card>
    );
  };

  return (
      <MDBox>
          <MDTypography variant="h5" fontWeight="bold">
            {title ?? 'Host Location Status'}
          </MDTypography>
          <MDTypography variant="button" color="text" mb={1} sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal',}}>
            {description ?? 'Current settings of each hosting location at a glance'}
          </MDTypography>
          {redirecting || loading ? (
            <MDBox display="flex" justifyContent="center" mt={4}>
              <CircularProgress sx={{ color: "#21b2a6" }} />
            </MDBox>
          ) : hostStats.length === 0 ? (
            <MDBox display="flex" justifyContent="center" alignItems="center" mt={4}>
              <MDTypography>No host stats available.</MDTypography>
            </MDBox>
          ) : (
              hostStats.map((item) => (
                <MDBox>
                  {renderHostCard(item)}
                </MDBox>
              ))
          )}
      </MDBox>

  );
};

export default CorporateHostPerformance;
